import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useQueries, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inventoryApi, sourcesApi, cbomApi } from '@/api/clients/apiClient'
import { useTheme } from '@/contexts/ThemeContext'
import { CertExpiryChips, CertExpiryBarChart, CertCaHierarchy } from '@/components/common/CertInventoryViews'
import { getDaysUntilExpiry } from '@/utils/certUtils'
import { useOrgStore } from '@/stores/orgStore'
import type { MemberSource, SourceRisk, RiskSummary, PackageFinding, QuantumRisk, AssetType, Severity, Modality, FindingsSummary, PackageSummary, PackageStats, CryptoStats } from '@/api/types'
import { RiskBadge, SeverityBadge } from '@/pages/Packages'
import { QuantumSafeBadge, ModalityBadge } from '@/components/common/StatusBadge'
import VexPanel from '@/components/common/VexPanel'
import { CryptoAssetsTable, FindingsSummaryBar } from '@/components/common/CryptoAssetsTable'
import { InventoryModal } from '@/components/common/InventoryModal'
import { AddSourceModal } from '@/pages/Sources'

type Tab = 'overview' | 'sources' | 'packages' | 'crypto' | 'sbom' | 'cbom'

const RISK_KEYS: (keyof RiskSummary)[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE', 'UNKNOWN']

const RISK_KEY_COLORS: Record<keyof RiskSummary, string> = {
  CRITICAL: 'bg-error/15 text-error dark:bg-error/25 dark:text-red-300',
  HIGH:     'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  MEDIUM:   'bg-warning/15 text-yellow-800 dark:bg-warning/20 dark:text-yellow-300',
  LOW:      'bg-success/10 text-green-800 dark:bg-success/15 dark:text-green-300',
  NONE:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  UNKNOWN:  'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark',
}

const PKG_RISK_LEVELS: QuantumRisk[] = ['vulnerable', 'partial', 'hybrid', 'safe', 'unknown']


const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'sources', label: 'Discovery Sources' },
  { key: 'crypto', label: 'Crypto Assets' },
  { key: 'packages', label: 'Packages' },
  { key: 'sbom', label: 'SBOM' },
  { key: 'cbom', label: 'CBOM' },
]

const ASSET_TYPES: AssetType[] = ['algorithm', 'certificate', 'key', 'protocol', 'kdf']
const CRYPTO_SEVERITY_LEVELS: Severity[] = ['critical', 'high', 'medium', 'low', 'info']
const MODALITIES: Modality[] = ['source', 'binary', 'runtime', 'network', 'infra', 'manual']
const PRIMITIVES = ['ae', 'dh', 'dsa', 'ec', 'ekdf', 'hash', 'kdf', 'mac', 'rsa', 'signature', 'xof']
const USAGE_CONTEXTS = ['transport', 'storage', 'signing', 'auth']

// ── Helpers ───────────────────────────────────────────────────────────────────

function latestScanAt(sources: MemberSource[]): { ts: string; sourceName: string } | null {
  let best: { ts: string; sourceName: string } | null = null
  for (const s of sources) {
    if (s.last_scanned_at && (!best || s.last_scanned_at > best.ts)) {
      best = { ts: s.last_scanned_at, sourceName: s.name }
    }
  }
  return best
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function InventoryDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentOrgId } = useOrgStore()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<Tab>('overview')
  const [showAddSources, setShowAddSources] = useState(false)
  const [showCreateSource, setShowCreateSource] = useState(false)
  const [addSourceFilter, setAddSourceFilter] = useState('')
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([])
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [downloadingCbomExport, setDownloadingCbomExport] = useState<'cbom' | 'findings' | 'sarif' | null>(null)

  const [pkgSearch, setPkgSearch] = useState('')
  const [pkgQRisk, setPkgQRisk] = useState<QuantumRisk | ''>('')
  const [pkgEcosystem, setPkgEcosystem] = useState('')
  const [pkgScope, setPkgScope] = useState<'all' | 'direct' | 'transitive'>('all')
  const [pkgSafety, setPkgSafety] = useState<'all' | 'safe' | 'unsafe'>('all')
  const [pkgSeverity, setPkgSeverity] = useState<Severity | ''>('')
  const [pkgAlgorithm, setPkgAlgorithm] = useState('')
  const [expandedPkgId, setExpandedPkgId] = useState<string | null>(null)
  const [pkgPages, setPkgPages] = useState<PackageFinding[][]>([])
  const [pkgOffset, setPkgOffset] = useState(0)
  const [pkgHasMore, setPkgHasMore] = useState(false)
  const [pkgTotal, setPkgTotal] = useState(0)
  const [pkgSummary, setPkgSummary] = useState<PackageSummary | null>(null)
  const pkgSentinelRef = useRef<HTMLDivElement>(null)
  const sbomSentinelRef = useRef<HTMLDivElement>(null)
  const cbomSentinelRef = useRef<HTMLDivElement>(null)
  const pkgIsFetchingRef = useRef(false)
  const [cryptoSearch, setCryptoSearch] = useState('')
  const [cryptoAssetType, setCryptoAssetType] = useState<AssetType | '' | 'others'>('')
  const [cryptoSeverity, setCryptoSeverity] = useState<Severity | ''>('')
  const [cryptoSafety, setCryptoSafety] = useState<'all' | 'safe' | 'unsafe'>('all')
  const [cryptoQRisk, setCryptoQRisk] = useState<QuantumRisk | ''>('')
  const [cryptoModality, setCryptoModality] = useState<Modality | ''>('')
  const [cryptoPrimitive, setCryptoPrimitive] = useState('')
  const [cryptoUsageContext, setCryptoUsageContext] = useState('')
  const [cryptoSummary, setCryptoSummary] = useState<FindingsSummary | null>(null)
  const [certExpiredOnly, setCertExpiredOnly] = useState(false)
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const invQueryKey = ['inventory', currentOrgId, id]

  const { data: inv, isLoading, isError, error } = useQuery({
    queryKey: invQueryKey,
    queryFn: () => inventoryApi.get(currentOrgId, id!),
    enabled: !!id,
  })

  // Always fetch risk — needed for Overview quick strip + Sources SBOM version column
  const { data: risk, isLoading: riskLoading } = useQuery({
    queryKey: ['inventory-risk', currentOrgId, id],
    queryFn: () => inventoryApi.getRisk(currentOrgId, id!),
    enabled: !!id && !!inv,
  })

  const { data: riskHistory } = useQuery({
    queryKey: ['inventory-risk-history', currentOrgId, id],
    queryFn: () => inventoryApi.getRiskHistory(currentOrgId, id!),
    enabled: !!id && !!inv && tab === 'overview',
  })

  const SBOM_VERSION_PAGE_SIZE = 20

  const {
    data: sbomVersionsData,
    fetchNextPage: fetchNextSbomPage,
    hasNextPage: hasSbomNextPage,
    isFetchingNextPage: isFetchingNextSbomPage,
  } = useInfiniteQuery({
    queryKey: ['inventory-composite-sbom-versions', currentOrgId, id],
    queryFn: ({ pageParam }) =>
      inventoryApi.getInventoryCompositeSbomVersions(currentOrgId, id!, {
        offset: pageParam,
        limit: SBOM_VERSION_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + (p.versions?.length ?? 0), 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    enabled: !!id && !!inv && tab === 'sbom',
  })

  const sbomVersions = sbomVersionsData?.pages.flatMap(p => p.versions ?? []) ?? []
  const sbomVersionsTotal = sbomVersionsData?.pages[0]?.total ?? 0

  const CBOM_VERSION_PAGE_SIZE = 20

  const {
    data: cbomVersionsData,
    fetchNextPage: fetchNextCbomPage,
    hasNextPage: hasCbomNextPage,
    isFetchingNextPage: isFetchingNextCbomPage,
  } = useInfiniteQuery({
    queryKey: ['inventory-composite-cbom-versions', currentOrgId, id],
    queryFn: ({ pageParam }) =>
      inventoryApi.getInventoryCompositeCbomVersions(currentOrgId, id!, {
        offset: pageParam,
        limit: CBOM_VERSION_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + (p.versions?.length ?? 0), 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    enabled: !!id && !!inv && tab === 'cbom',
  })

  const cbomVersions = cbomVersionsData?.pages.flatMap(p => p.versions ?? []) ?? []
  const cbomVersionsTotal = cbomVersionsData?.pages[0]?.total ?? 0

  const PKG_PAGE_SIZE = 50

  useEffect(() => {
    setPkgOffset(0)
    setPkgHasMore(false)
  }, [pkgSearch, pkgQRisk, pkgEcosystem, pkgScope, pkgSafety, pkgAlgorithm])

  const { isLoading: pkgLoading, isFetching: pkgFetching, data: pkgReport } = useQuery({
    queryKey: ['inventory-pkg-findings', currentOrgId, id, pkgSearch, pkgQRisk, pkgEcosystem, pkgScope, pkgSafety, pkgAlgorithm, pkgOffset],
    queryFn: () => inventoryApi.fetchPackageFindings(currentOrgId, id!, {
      limit: PKG_PAGE_SIZE,
      offset: pkgOffset,
      name: pkgSearch || undefined,
      quantum_risk: pkgQRisk || undefined,
      ecosystem: pkgEcosystem || undefined,
      scope: pkgScope === 'all' ? undefined : pkgScope,
      is_quantum_safe: pkgSafety === 'all' ? undefined : pkgSafety === 'safe',
      algorithm: pkgAlgorithm || undefined,
    }),
    enabled: !!currentOrgId && !!id && tab === 'packages',
  })

  useEffect(() => {
    if (!pkgReport) return
    const batch = pkgReport.findings ?? []
    setPkgPages((prev) => {
      if (pkgOffset === 0) return [batch]
      const next = [...prev]
      next[pkgOffset / PKG_PAGE_SIZE] = batch
      return next
    })
    setPkgHasMore(pkgOffset + batch.length < pkgReport.total)
    setPkgTotal(pkgReport.total)
    if (pkgOffset === 0) setPkgSummary(pkgReport.summary)
  }, [pkgReport, pkgOffset])

  pkgIsFetchingRef.current = pkgFetching

  const pkgLoadMore = useCallback(() => setPkgOffset((p) => p + PKG_PAGE_SIZE), [])

  useEffect(() => {
    const el = pkgSentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pkgHasMore && !pkgIsFetchingRef.current) pkgLoadMore()
      },
      { rootMargin: '600px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [pkgHasMore, pkgLoadMore])

  useEffect(() => {
    const el = sbomSentinelRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasSbomNextPage && !isFetchingNextSbomPage) fetchNextSbomPage()
      },
      { threshold: 0.1 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [hasSbomNextPage, isFetchingNextSbomPage, fetchNextSbomPage])

  useEffect(() => {
    const el = cbomSentinelRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasCbomNextPage && !isFetchingNextCbomPage) fetchNextCbomPage()
      },
      { threshold: 0.1 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [hasCbomNextPage, isFetchingNextCbomPage, fetchNextCbomPage])

  const { data: cryptoReport, isLoading: cryptoLoading } = useQuery({
    queryKey: ['inventory-crypto-findings', currentOrgId, id, cryptoSearch, cryptoAssetType, cryptoSeverity, cryptoSafety, cryptoQRisk, cryptoModality, cryptoPrimitive, cryptoUsageContext],
    queryFn: () => inventoryApi.fetchFindings(currentOrgId, id!, {
      limit: 200,
      name: cryptoSearch || undefined,
      asset_type: (cryptoAssetType === 'others' ? undefined : cryptoAssetType) || undefined,
      severity: cryptoSeverity || undefined,
      is_quantum_safe: cryptoSafety === 'all' ? undefined : cryptoSafety === 'safe',
      quantum_risk: cryptoQRisk || undefined,
      modality: cryptoModality || undefined,
      primitive: cryptoPrimitive || undefined,
      usage_context: cryptoUsageContext || undefined,
    }),
    enabled: !!currentOrgId && !!id && tab === 'crypto',
  })

  useEffect(() => {
    if (cryptoReport && cryptoReport.summary) setCryptoSummary(cryptoReport.summary)
  }, [cryptoReport])

  const { data: sourcesResp } = useQuery({
    queryKey: ['sources', currentOrgId],
    queryFn: () => sourcesApi.list(currentOrgId),
    enabled: showAddSources,
  })

  const { data: allInventoriesResp } = useQuery({
    queryKey: ['inventories', currentOrgId],
    queryFn: () => inventoryApi.list(currentOrgId, { page_size: 200 }),
    enabled: showAddSources,
  })

  const otherInventoryIds = (allInventoriesResp?.inventories ?? [])
    .map((i) => i.id)
    .filter((invId) => invId !== id)

  const otherInventoryQueries = useQueries({
    queries: otherInventoryIds.map((invId) => ({
      queryKey: ['inventory', currentOrgId, invId],
      queryFn: () => inventoryApi.get(currentOrgId, invId),
      enabled: showAddSources,
    })),
  })

  const takenSourceMap = new Map<string, string>(
    otherInventoryQueries
      .filter((q) => q.data)
      .flatMap((q) => q.data!.sources.map((s): [string, string] => [s.id, q.data!.name]))
  )

  const allSources = sourcesResp?.data ?? []
  const memberIds = new Set((inv?.sources ?? []).map((s) => s.id))
  const addableSources = allSources.filter((s) => !memberIds.has(s.id))
  const invSourceMap = Object.fromEntries((inv?.sources ?? []).map((s) => [s.id, s.name]))

  const visiblePkgs = pkgPages.flat().filter(pkg => !pkgSeverity || pkg.severity === pkgSeverity)

  // ── Mutations ────────────────────────────────────────────────────────────────

  const addSourcesMutation = useMutation({
    mutationFn: (sourceIds: string[]) =>
      inventoryApi.addSources(currentOrgId, id!, { source_ids: sourceIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invQueryKey })
      queryClient.invalidateQueries({ queryKey: ['inventory-risk', currentOrgId, id] })
      setShowAddSources(false)
      setSelectedSourceIds([])
      setAddSourceFilter('')
    },
  })

  const removeSourceMutation = useMutation({
    mutationFn: (sourceId: string) => inventoryApi.removeSource(currentOrgId, id!, sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invQueryKey })
      queryClient.invalidateQueries({ queryKey: ['inventory-risk', currentOrgId, id] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => inventoryApi.delete(currentOrgId, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventories', currentOrgId] })
      navigate('/inventories', { replace: true })
    },
  })

  // ── Helpers ──────────────────────────────────────────────────────────────────



  async function handleDownload(format: 'cyclonedx' | 'spdx' | 'swid') {
    setDownloading(format)
    try { await inventoryApi.downloadCompositeSbom(currentOrgId, id!, format, inv?.name) }
    finally { setDownloading(null) }
  }

  async function handleCbomExport(type: 'cbom' | 'findings' | 'sarif') {
    setDownloadingCbomExport(type)
    try {
      if (type === 'cbom') await inventoryApi.downloadCompositeCbom(currentOrgId, id!, inv?.name)
      else if (type === 'findings') await inventoryApi.downloadCompositeFindings(currentOrgId, id!, inv?.name)
      else await inventoryApi.downloadCompositeSarif(currentOrgId, id!, inv?.name)
    } finally { setDownloadingCbomExport(null) }
  }


  function toggleSourceSelect(sourceId: string) {
    setSelectedSourceIds((prev) =>
      prev.includes(sourceId) ? prev.filter((x) => x !== sourceId) : [...prev, sourceId]
    )
  }

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-container-light dark:bg-container-dark rounded w-48 animate-pulse" />
        <div className="h-4 bg-container-light dark:bg-container-dark rounded w-80 animate-pulse" />
      </div>
    )
  }

  if (isError || !inv) {
    return (
      <div className="space-y-4">
        <Link to="/inventories" className="flex items-center gap-1.5 text-sm text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark">
          <span className="material-icons text-base">arrow_back</span>
          Inventories
        </Link>
        <div className="flex items-start gap-3 p-4 bg-error/5 dark:bg-error/10 border border-error/20 dark:border-error/25 rounded-xl">
          <span className="material-icons text-error text-xl mt-0.5">error_outline</span>
          <p className="text-sm text-error">{(error as Error)?.message ?? 'Inventory not found'}</p>
        </div>
      </div>
    )
  }

  const latestScan = latestScanAt(inv.sources)
  const neverScannedCount = inv.sources.filter((s) => !s.last_scanned_at).length
  const pkgTabLabel = pkgTotal > 0 ? `Packages (${pkgTotal})` : 'Packages'
  const cryptoTabLabel = cryptoReport ? `Crypto Assets (${cryptoReport.total})` : 'Crypto Assets'

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6" id="inventory-details-page">
      {/* Breadcrumb */}
      <Link
        to="/inventories"
        className="flex items-center gap-1.5 text-sm text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark w-fit"
      >
        <span className="material-icons text-base">arrow_back</span>
        Inventories
      </Link>

      {/* Header — lean: name + edit + delete only */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">{inv.name}</h1>
            <button
              onClick={() => setShowEditModal(true)}
              className="p-1 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/5"
              title="Edit"
            >
              <span className="material-icons text-base">edit</span>
            </button>
          </div>
          {inv.description && (
            <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">{inv.description}</p>
          )}
          {inv.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {inv.tags.map((t) => (
                <span key={t} className="px-2.5 py-0.5 text-xs rounded-full bg-primary/10 dark:bg-secondary/15 text-primary dark:text-secondary border border-primary/20 dark:border-secondary/25 font-medium">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-error hover:bg-error/5"
          title="Delete inventory"
        >
          <span className="material-icons text-base">delete_outline</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-border-light dark:border-border-dark">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                tab === key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
              }`}
            >
              {key === 'packages' ? pkgTabLabel : key === 'crypto' ? cryptoTabLabel : label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab: Overview ───────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div className="space-y-6">
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
              <p className="text-xs text-subtle-light dark:text-subtle-dark mb-1">Sources</p>
              <p className="text-2xl font-bold text-text-light dark:text-text-dark">{inv.sources.length}</p>
            </div>
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
              <p className="text-xs text-subtle-light dark:text-subtle-dark mb-1">Created</p>
              <p className="text-sm font-medium text-text-light dark:text-text-dark">{formatDate(inv.created_at)}</p>
            </div>
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
              <p className="text-xs text-subtle-light dark:text-subtle-dark mb-1">Updated</p>
              <p className="text-sm font-medium text-text-light dark:text-text-dark">{formatDate(inv.updated_at)}</p>
            </div>
          </div>

          {/* Metadata card */}
          {inv.metadata && Object.values(inv.metadata).some((v) => v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0)) && (
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border-light dark:border-border-dark">
                <p className="text-sm font-semibold text-text-light dark:text-text-dark">Metadata</p>
              </div>
              <div className="p-4 space-y-4">
                {([
                  ['Identity', [
                    ['system_name', 'System Name'],
                    ['business_use_case', 'Business Use Case'],
                    ['environment', 'Environment'],
                    ['lifecycle_stage', 'Lifecycle Stage'],
                  ]],
                  ['Ownership', [
                    ['business_owner', 'Business Owner'],
                    ['technology_owner', 'Technology Owner'],
                    ['security_owner', 'Security Owner'],
                    ['support_group', 'Support Group'],
                    ['business_unit', 'Business Unit'],
                    ['department', 'Department'],
                  ]],
                  ['Risk Classification', [
                    ['criticality', 'Criticality'],
                    ['data_classification', 'Data Classification'],
                    ['regulatory_scope', 'Regulatory Scope'],
                    ['sla_tier', 'SLA Tier'],
                  ]],
                  ['CMDB', [
                    ['cmdb_ref', 'CMDB Reference'],
                  ]],
                ] as [string, [string, string][]][]).map(([groupLabel, fields]) => {
                  const visibleFields = fields.filter(([key]) => {
                    const val = (inv.metadata as Record<string, unknown>)[key]
                    return val !== undefined && val !== '' && (!Array.isArray(val) || val.length > 0)
                  })
                  if (!visibleFields.length) return null
                  return (
                    <div key={groupLabel}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">{groupLabel}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                        {visibleFields.map(([key, label]) => {
                          const val = (inv.metadata as Record<string, unknown>)[key]
                          return (
                            <div key={key}>
                              <p className="text-xs text-subtle-light dark:text-subtle-dark">{label}</p>
                              {Array.isArray(val) ? (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {(val as string[]).map((v) => (
                                    <span key={v} className="px-1.5 py-0.5 text-xs rounded bg-container-light dark:bg-container-dark text-text-light dark:text-text-dark">{v}</span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm font-medium text-text-light dark:text-text-dark">{String(val)}</p>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Recent Scan Strip */}
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl p-4 space-y-3">
            <p className="text-sm font-semibold text-text-light dark:text-text-dark">Recent Scan Activity</p>
            {latestScan ? (
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="material-icons text-success text-base">check_circle</span>
                  <span className="text-sm text-text-light dark:text-text-dark">
                    Last scanned <span className="font-medium">{formatRelative(latestScan.ts)}</span>
                  </span>
                  <span className="text-xs text-subtle-light dark:text-subtle-dark">
                    via {latestScan.sourceName}
                  </span>
                </div>
                {neverScannedCount > 0 && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-warning/10 text-warning text-xs font-medium">
                    <span className="material-icons text-sm">warning_amber</span>
                    {neverScannedCount} source{neverScannedCount > 1 ? 's' : ''} never scanned
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-subtle-light dark:text-subtle-dark">
                <span className="material-icons text-base">schedule</span>
                No scans yet. Add sources and trigger a scan.
              </div>
            )}
            {/* Per-source latest scan timestamps */}
            {inv.sources.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                {inv.sources.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-xs bg-container-light dark:bg-container-dark rounded-lg px-3 py-2">
                    <Link to={`/sources/${s.id}`} className="font-medium text-text-light dark:text-text-dark hover:text-primary truncate max-w-[160px]">
                      {s.name}
                    </Link>
                    <span className="text-subtle-light dark:text-subtle-dark shrink-0">
                      {s.last_scanned_at ? formatRelative(s.last_scanned_at) : 'never'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Risk Summary */}
          {riskLoading && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-24 bg-container-light dark:bg-container-dark rounded-xl animate-pulse" />
              ))}
            </div>
          )}

          {risk && (
            <>
              {risk.is_pqc_compliant && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                  <span className="material-icons text-sm">verified</span>
                  PQC Compliant
                </div>
              )}

              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {RISK_KEYS.map((key) => (
                  <RiskSummaryCard key={key} label={key} count={risk.summary[key] ?? 0} />
                ))}
              </div>

              {(hasSummaryData(risk.package_summary) || hasSummaryData(risk.crypto_summary)) && (
                <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-light dark:border-border-dark">
                        <th className="text-left px-4 py-3 text-xs font-medium text-subtle-light dark:text-subtle-dark w-28">Breakdown</th>
                        {RISK_KEYS.map((k) => (
                          <th key={k} className="text-center px-3 py-3 text-xs font-medium text-subtle-light dark:text-subtle-dark">{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <RiskBreakdownRow label="Packages" icon="inventory_2" summary={risk.package_summary} />
                      <RiskBreakdownRow label="Crypto" icon="key" summary={risk.crypto_summary} />
                    </tbody>
                  </table>
                </div>
              )}

              {(risk.package_stats || risk.crypto_stats) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {risk.package_stats && <PackagePostureCard stats={risk.package_stats} dataAgeDays={risk.data_age_days} />}
                  {risk.crypto_stats && <CryptoPostureCard stats={risk.crypto_stats} dataAgeDays={risk.data_age_days} />}
                </div>
              )}

              {(risk.sources ?? []).length > 0 ? (
                <div>
                  <h2 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">Per-Source Breakdown</h2>
                  <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border-light dark:border-border-dark">
                          <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Source</th>
                          {RISK_KEYS.map((k) => (
                            <th key={k} className="text-center px-3 py-3 font-medium text-subtle-light dark:text-subtle-dark text-xs">
                              {k}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(risk.sources ?? []).map((s) => (
                          <SourceRiskRow
                            key={s.source_id}
                            sourceRisk={s}
                            sourceName={
                              s.source_name ||
                              inv.sources.find((ms) => ms.id === s.source_id)?.name ||
                              s.source_id
                            }
                            sourceId={s.source_id}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 bg-secondary/10 dark:bg-secondary/15 border border-secondary/25 dark:border-secondary/30 rounded-xl">
                  <span className="material-icons text-primary dark:text-secondary text-xl mt-0.5">info_outline</span>
                  <p className="text-sm text-text-light dark:text-text-dark">
                    No risk data yet. Add sources and run scans to see quantum risk across this inventory.
                  </p>
                </div>
              )}
              {riskHistory && riskHistory.snapshots.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">Risk Trend</h2>
                  <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border-light dark:border-border-dark">
                          <th className="px-4 py-2.5 text-left font-medium text-subtle-light dark:text-subtle-dark">Date</th>
                          <th className="px-4 py-2.5 text-right font-medium text-subtle-light dark:text-subtle-dark">Crit+High</th>
                          <th className="px-4 py-2.5 text-right font-medium text-subtle-light dark:text-subtle-dark">Pkg %</th>
                          <th className="px-4 py-2.5 text-right font-medium text-subtle-light dark:text-subtle-dark">Crypto %</th>
                          <th className="px-4 py-2.5 text-right font-medium text-subtle-light dark:text-subtle-dark">PQC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...riskHistory.snapshots].reverse().slice(0, 10).map((s) => (
                          <tr key={s.id} className="border-b border-border-light dark:border-border-dark last:border-0">
                            <td className="px-4 py-2.5 text-text-light dark:text-text-dark">
                              {new Date(s.snapshotted_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              {(s.summary.CRITICAL + s.summary.HIGH) > 0 ? (
                                <span className="font-medium text-error dark:text-red-400">{s.summary.CRITICAL + s.summary.HIGH}</span>
                              ) : (
                                <span className="text-subtle-light dark:text-subtle-dark">0</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right text-text-light dark:text-text-dark">
                              {Math.round(s.package_stats.migration_progress)}%
                            </td>
                            <td className="px-4 py-2.5 text-right text-text-light dark:text-text-dark">
                              {Math.round(s.crypto_stats.migration_progress)}%
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              {s.is_pqc_compliant ? (
                                <span className="font-medium text-success-light dark:text-success-dark">Yes</span>
                              ) : (
                                <span className="text-subtle-light dark:text-subtle-dark">No</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      )}

      {/* ── Tab: Sources ────────────────────────────────────────────────────── */}
      {tab === 'sources' && (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowCreateSource(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg hover:border-primary hover:text-primary text-subtle-light dark:text-subtle-dark"
            >
              <span className="material-icons text-base">add_circle_outline</span>
              New Source
            </button>
            <button
              onClick={() => setShowAddSources(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              <span className="material-icons text-base">add</span>
              Add Discovery Sources
            </button>
          </div>

          {inv.sources.length === 0 ? (
            <div className="flex items-start gap-3 p-4 bg-secondary/10 dark:bg-secondary/15 border border-secondary/25 dark:border-secondary/30 rounded-xl">
              <span className="material-icons text-primary dark:text-secondary text-xl mt-0.5">info_outline</span>
              <p className="text-sm text-text-light dark:text-text-dark">
                No sources in this inventory yet. Add sources to generate a Composite SBOM.
              </p>
            </div>
          ) : (
            <MemberSourcesTable
              sources={inv.sources}
              sourceRisks={risk?.sources ?? []}
              onRemove={(sourceId) => removeSourceMutation.mutate(sourceId)}
              removingId={removeSourceMutation.isPending ? removeSourceMutation.variables : undefined}
            />
          )}
        </div>
      )}

      {/* ── Tab: Crypto Assets ──────────────────────────────────────────────── */}
      {tab === 'crypto' && (() => {
        const isCertView = cryptoAssetType === 'certificate'
        const baseFindings = cryptoAssetType === 'others'
          ? (cryptoReport?.findings ?? []).filter(f => !ASSET_TYPES.includes(f.asset_type as AssetType))
          : (cryptoReport?.findings ?? [])
        const displayFindings = isCertView && certExpiredOnly
          ? baseFindings.filter(f => { const d = getDaysUntilExpiry(f); return d !== null && d <= 0 })
          : baseFindings
        return (
          <div className="space-y-4">
            {cryptoSummary && cryptoSummary.total > 0 && (
              <FindingsSummaryBar
                total={cryptoSummary.total}
                quantumSafe={cryptoSummary.quantum_safe}
                quantumUnsafe={cryptoSummary.quantum_unsafe}
                byRisk={cryptoSummary.by_risk}
                byLifecycle={cryptoSummary.by_lifecycle}
              />
            )}

            {/* Certificate expiry chips + bar chart */}
            {isCertView && baseFindings.length > 0 && (
              <>
                <CertExpiryChips findings={baseFindings} />
                <CertExpiryBarChart findings={baseFindings} isDark={isDark} />
              </>
            )}

            {/* Filters */}
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
              <div className="flex flex-wrap gap-3">
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={cryptoSearch}
                  onChange={(e) => setCryptoSearch(e.target.value)}
                  className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-52 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <select
                  value={cryptoAssetType}
                  onChange={(e) => { setCryptoAssetType(e.target.value as AssetType | '' | 'others'); setCertExpiredOnly(false) }}
                  className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                >
                  <option value="">All Types</option>
                  {ASSET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  <option value="others">others</option>
                </select>
                <select
                  value={cryptoSeverity}
                  onChange={(e) => setCryptoSeverity(e.target.value as Severity | '')}
                  className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                >
                  <option value="">All Severities</option>
                  {CRYPTO_SEVERITY_LEVELS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select
                  value={cryptoSafety}
                  onChange={(e) => setCryptoSafety(e.target.value as 'all' | 'safe' | 'unsafe')}
                  className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                >
                  <option value="all">All Quantum Status</option>
                  <option value="safe">Quantum Safe</option>
                  <option value="unsafe">At Risk</option>
                </select>
                <select
                  value={cryptoQRisk}
                  onChange={(e) => setCryptoQRisk(e.target.value as QuantumRisk | '')}
                  className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                >
                  <option value="">All Quantum Risk</option>
                  {PKG_RISK_LEVELS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select
                  value={cryptoModality}
                  onChange={(e) => setCryptoModality(e.target.value as Modality | '')}
                  className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                >
                  <option value="">All Modalities</option>
                  {MODALITIES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select
                  value={cryptoPrimitive}
                  onChange={(e) => setCryptoPrimitive(e.target.value)}
                  className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                >
                  <option value="">All Primitives</option>
                  {PRIMITIVES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <select
                  value={cryptoUsageContext}
                  onChange={(e) => setCryptoUsageContext(e.target.value)}
                  className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                >
                  <option value="">All Usage Contexts</option>
                  {USAGE_CONTEXTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {isCertView && (
                  <label className="flex items-center gap-2 px-3 py-2 text-sm text-text-light dark:text-text-dark cursor-pointer">
                    <input
                      type="checkbox"
                      checked={certExpiredOnly}
                      onChange={(e) => setCertExpiredOnly(e.target.checked)}
                      className="rounded border-border-light dark:border-border-dark accent-error"
                    />
                    Expired only
                  </label>
                )}
                {(cryptoSearch || cryptoAssetType || cryptoSeverity || cryptoSafety !== 'all' || cryptoQRisk || cryptoModality || cryptoPrimitive || cryptoUsageContext || certExpiredOnly) && (
                  <button
                    onClick={() => { setCryptoSearch(''); setCryptoAssetType(''); setCryptoSeverity(''); setCryptoSafety('all'); setCryptoQRisk(''); setCryptoModality(''); setCryptoPrimitive(''); setCryptoUsageContext(''); setCertExpiredOnly(false) }}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
                  >
                    <span className="material-icons text-base">clear</span>
                    Clear
                  </button>
                )}
              </div>
              {cryptoReport && (
                <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
                  Showing {displayFindings.length} of {cryptoReport.total} assets
                  {cryptoReport.total > 200 && ' — refine filters to see more'}
                </p>
              )}
            </div>

            <CryptoAssetsTable
              findings={displayFindings}
              showSourceColumn
              sourceMap={invSourceMap}
              isLoading={cryptoLoading}
              emptyMessage={
                cryptoReport?.total === 0
                  ? 'No crypto assets found. Run scans on member sources.'
                  : 'No assets match the current filters.'
              }
            />

            {/* CA hierarchy — only in cert view */}
            {isCertView && !cryptoLoading && baseFindings.length > 0 && (
              <CertCaHierarchy findings={baseFindings} />
            )}
          </div>
        )
      })()}

      {/* ── Tab: Packages ───────────────────────────────────────────────────── */}
      {tab === 'packages' && (
        <div className="space-y-4">
          {/* Stat blocks */}
          {pkgSummary && (() => {
            const s = pkgSummary
            const byQR = s.by_quantum_risk ?? {}
            const safeCount = byQR['safe'] ?? 0
            const atRisk = s.total - safeCount
            const highCritical = byQR['vulnerable'] ?? 0
            const safePercent = s.total ? Math.round((safeCount / s.total) * 100) : 0
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
                  <p className="label-sm text-subtle-light dark:text-subtle-dark">Total Packages</p>
                  <p className="text-2xl font-bold tracking-display text-text-light dark:text-text-dark mt-1">{s.total}</p>
                </div>
                <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
                  <p className="label-sm text-subtle-light dark:text-subtle-dark">Quantum Safe</p>
                  <p className="text-2xl font-bold tracking-display text-success mt-1">
                    {safeCount}
                    <span className="text-sm font-normal text-subtle-light dark:text-subtle-dark ml-1">({safePercent}%)</span>
                  </p>
                </div>
                <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
                  <p className="label-sm text-subtle-light dark:text-subtle-dark">At Risk</p>
                  <p className="text-2xl font-bold tracking-display text-error mt-1">{atRisk}</p>
                </div>
                <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
                  <p className="label-sm text-subtle-light dark:text-subtle-dark">Vulnerable</p>
                  <p className="text-2xl font-bold tracking-display text-warning mt-1">{highCritical}</p>
                </div>
              </div>
            )
          })()}

          {/* Filters */}
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
            <div className="flex flex-wrap gap-3">
              <input
                type="text"
                placeholder="Search by name or PURL…"
                value={pkgSearch}
                onChange={(e) => setPkgSearch(e.target.value)}
                className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-60 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <select
                value={pkgQRisk}
                onChange={(e) => setPkgQRisk(e.target.value as QuantumRisk | '')}
                className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
              >
                <option value="">All Quantum Risk</option>
                {PKG_RISK_LEVELS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <select
                value={pkgSeverity}
                onChange={(e) => setPkgSeverity(e.target.value as Severity | '')}
                className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
              >
                <option value="">All Severities</option>
                {(['critical', 'high', 'medium', 'low', 'info'] as Severity[]).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Ecosystem…"
                value={pkgEcosystem}
                onChange={(e) => setPkgEcosystem(e.target.value)}
                className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-36 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <select
                value={pkgScope}
                onChange={(e) => setPkgScope(e.target.value as 'all' | 'direct' | 'transitive')}
                className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
              >
                <option value="all">All Scope</option>
                <option value="direct">Direct</option>
                <option value="transitive">Transitive</option>
              </select>
              <select
                value={pkgSafety}
                onChange={(e) => setPkgSafety(e.target.value as 'all' | 'safe' | 'unsafe')}
                className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
              >
                <option value="all">All Quantum Status</option>
                <option value="safe">Safe</option>
                <option value="unsafe">At Risk</option>
              </select>
              <input
                type="text"
                placeholder="Algorithm (e.g. RSA-2048)…"
                value={pkgAlgorithm}
                onChange={(e) => setPkgAlgorithm(e.target.value)}
                className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-48 focus:outline-none focus:ring-2 focus:ring-primary/50"
                title="Filter by linked crypto algorithm name"
              />
            </div>
          </div>

          {pkgLoading && pkgPages.length === 0 && (
            <div className="flex items-center justify-center h-32">
              <span className="material-icons animate-spin text-primary text-3xl">sync</span>
            </div>
          )}

          {!pkgFetching && visiblePkgs.length === 0 && (
            <div className="text-center py-12">
              <span className="material-icons text-3xl text-subtle-light dark:text-subtle-dark opacity-40">inventory_2</span>
              <p className="text-sm text-subtle-light dark:text-subtle-dark mt-2">
                {pkgTotal > 0 ? 'No packages match the current filters.' : 'No packages found.'}
              </p>
            </div>
          )}

          {visiblePkgs.length > 0 && (
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-light dark:border-border-dark">
                    <th className="w-8 px-3 py-3" />
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Package</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Version</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Ecosystem</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Modality</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Scope</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Severity</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Quantum Risk</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Quantum Safety</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Evidence</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden lg:table-cell">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-light dark:divide-border-dark">
                  {visiblePkgs.map((pkg, i) => (
                    <Fragment key={pkg.id}>
                      <tr
                        className={`cursor-pointer transition-colors hover:bg-background-light dark:hover:bg-background-dark ${i % 2 === 1 ? 'bg-background-light/50 dark:bg-background-dark/50' : ''}`}
                        onClick={() => setExpandedPkgId((prev) => (prev === pkg.id ? null : pkg.id))}
                      >
                        <td className="px-3 py-3 text-center">
                          <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">
                            {expandedPkgId === pkg.id ? 'expand_less' : 'expand_more'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-text-light dark:text-text-dark truncate max-w-[200px]">{pkg.name}</p>
                          {pkg.purl && (
                            <p className="font-mono text-xs text-subtle-light dark:text-subtle-dark truncate max-w-[200px]" title={pkg.purl}>{pkg.purl}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark hidden sm:table-cell">{pkg.version || '—'}</td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark hidden md:table-cell">{pkg.ecosystem || '—'}</td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {pkg.modality ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                              pkg.modality === 'binary'  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                              pkg.modality === 'network' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                           'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark'
                            }`}>
                              {pkg.modality}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                            pkg.is_direct ? 'bg-primary/10 dark:bg-secondary/15 text-primary dark:text-secondary' : 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark'
                          }`}>
                            {pkg.is_direct ? 'Direct' : 'Transitive'}
                          </span>
                        </td>
                        <td className="px-4 py-3"><SeverityBadge level={pkg.severity} /></td>
                        <td className="px-4 py-3"><RiskBadge risk={pkg.quantum_risk} /></td>
                        <td className="px-4 py-3"><QuantumSafeBadge isSafe={pkg.is_quantum_safe} /></td>
                        <td className="px-4 py-3 text-xs text-subtle-light dark:text-subtle-dark hidden md:table-cell max-w-[200px]">
                          <div className="flex flex-col gap-1">
                            {pkg.linked_assets?.length ? (
                              <span className="font-mono" title={pkg.linked_assets.map((e) => e.name).join(', ')}>
                                {pkg.linked_assets.length} crypto link{pkg.linked_assets.length !== 1 ? 's' : ''}
                              </span>
                            ) : null}
                            {pkg.evidence?.occurrences?.length ? (
                              <span className="font-mono truncate block" title={pkg.evidence.occurrences[0].location}>{pkg.evidence.occurrences[0].location}</span>
                            ) : null}
                            {!pkg.linked_assets?.length && !pkg.evidence?.occurrences?.length ? '—' : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs hidden lg:table-cell">
                          {invSourceMap[pkg.source_id] ? (
                            <Link
                              to={`/sources/${pkg.source_id}`}
                              className="text-primary hover:underline font-mono"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {invSourceMap[pkg.source_id]}
                            </Link>
                          ) : (
                            <span className="font-mono text-subtle-light dark:text-subtle-dark">{pkg.source_id.slice(0, 8)}…</span>
                          )}
                        </td>
                      </tr>
                      {expandedPkgId === pkg.id && (
                        <tr className="bg-container-light dark:bg-container-dark">
                          <td />
                          <td colSpan={9} className="px-4 py-4">
                            <div className="space-y-4">
                              {pkg.linked_assets?.length ? (
                                <VexPanel evidence={pkg.linked_assets} />
                              ) : null}
                              {pkg.evidence?.occurrences?.length ? (
                                <div>
                                  <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-2">Evidence ({pkg.evidence.occurrences.length})</p>
                                  <ul className="space-y-1">
                                    {pkg.evidence.occurrences.map((o, j) => (
                                      <li key={j} className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-3 py-1.5">
                                        <span className="text-primary">{o.location}</span>
                                        {o.additionalContext && <span className="block text-subtle-light dark:text-subtle-dark mt-0.5 break-all">{o.additionalContext}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                              {!pkg.linked_assets?.length && !pkg.evidence?.occurrences?.length && (
                                <p className="text-xs text-subtle-light dark:text-subtle-dark">No evidence available.</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div ref={pkgSentinelRef} className="h-1" />
          {pkgFetching && pkgOffset > 0 && (
            <div className="flex items-center justify-center py-4">
              <span className="material-icons animate-spin text-primary text-2xl">sync</span>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: SBOM ───────────────────────────────────────────────────────── */}
      {tab === 'sbom' && (
        <div className="space-y-6">
          {/* Section A: Composite SBOM Export */}
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl p-5">
            <h2 className="text-sm font-semibold text-text-light dark:text-text-dark mb-1">Composite SBOM</h2>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mb-4">
              De-duplicated union of all member source SBOMs. De-duplication key: PURL. Worst-case quantum risk applied per package.
            </p>
            <div className="flex flex-wrap gap-2">
              {(['cyclonedx', 'spdx', 'swid'] as const).map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => handleDownload(fmt)}
                  disabled={!!downloading}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {downloading === fmt
                    ? <span className="material-icons text-base animate-spin">autorenew</span>
                    : <span className="material-icons text-base">download</span>}
                  {fmt === 'cyclonedx' ? 'CycloneDX' : fmt.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Section C: Per-Source SBOM History */}
          <div>
            <h2 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">Per-Source SBOM History</h2>
            {inv.sources.length === 0 ? (
              <div className="text-center py-10 text-subtle-light dark:text-subtle-dark text-sm">
                No sources in this inventory.
              </div>
            ) : (
              <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-container-light dark:bg-container-dark">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Source</th>
                      <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Modality</th>
                      <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Last Scanned</th>
                      <th className="text-right px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inv.sources.map((s) => (
                      <tr key={s.id} className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark">
                        <td className="px-4 py-3">
                          <Link to={`/sources/${s.id}`} className="font-medium text-text-light dark:text-text-dark hover:text-primary">
                            {s.name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark capitalize hidden sm:table-cell">
                          {s.modality}
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark text-xs">
                          {s.last_scanned_at
                            ? <span title={new Date(s.last_scanned_at).toLocaleString()}>{formatRelative(s.last_scanned_at)}</span>
                            : <span className="text-warning">Never scanned</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/sources/${s.id}/sbom-history`}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <span className="material-icons text-sm">history</span>
                            View History
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Section B: Composite SBOM Versions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-text-light dark:text-text-dark">Composite SBOM Versions</h2>
              <span className="text-xs text-subtle-light dark:text-subtle-dark">
                {sbomVersionsTotal} version{sbomVersionsTotal !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-container-light dark:bg-container-dark">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Version</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Created</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Components</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden lg:table-cell">Quantum Risk</th>
                    <th className="text-right px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Export</th>
                  </tr>
                </thead>
                <tbody>
                  {sbomVersions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-subtle-light dark:text-subtle-dark text-xs">
                        No composite SBOM versions yet. Run a scan to generate the first version.
                      </td>
                    </tr>
                  ) : (
                    sbomVersions.map((v, i) => (
                      <tr key={v.snapshot_id} className={`border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark ${i === 0 ? 'font-medium' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-text-light dark:text-text-dark">v{v.version}</span>
                            {i === 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">latest</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark text-xs hidden sm:table-cell">
                          {new Date(v.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark hidden md:table-cell">
                          {v.package_count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-success">{v.quantum_safe} safe</span>
                            <span className="text-subtle-light dark:text-subtle-dark">/</span>
                            <span className="text-error">{v.quantum_unsafe} unsafe</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {(['cyclonedx', 'spdx', 'swid'] as const).map((fmt) => (
                              <button
                                key={fmt}
                                onClick={() => inventoryApi.downloadInventoryCompositeSbomVersion(currentOrgId, id!, v.snapshot_id, fmt, inv.name)}
                                className="px-2 py-1 text-xs rounded hover:border-primary hover:text-primary text-subtle-light dark:text-subtle-dark transition-colors"
                              >
                                {fmt === 'cyclonedx' ? 'CDX' : fmt.toUpperCase()}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                  <tr>
                    <td colSpan={5} className="p-0">
                      <div ref={sbomSentinelRef} className="h-4" />
                    </td>
                  </tr>
                  {isFetchingNextSbomPage && (
                    <tr>
                      <td colSpan={5} className="py-2 text-center text-xs text-subtle-light dark:text-subtle-dark">
                        <span className="loading loading-spinner loading-xs mr-1" />Loading…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: CBOM ───────────────────────────────────────────────────────── */}
      {tab === 'cbom' && (
        <div className="space-y-6">
          {/* Section A: Composite CBOM Export */}
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl p-5">
            <h2 className="text-sm font-semibold text-text-light dark:text-text-dark mb-1">Composite CBOM</h2>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mb-4">
              Cryptographic asset inventory across all member sources. Includes algorithm, key, certificate, and protocol assets with quantum risk assessments.
            </p>
            <div className="flex flex-wrap gap-2">
              {(['cbom', 'findings', 'sarif'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => handleCbomExport(type)}
                  disabled={!!downloadingCbomExport}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {downloadingCbomExport === type
                    ? <span className="material-icons text-base animate-spin">autorenew</span>
                    : <span className="material-icons text-base">download</span>}
                  {type === 'cbom' ? 'CycloneDX' : type === 'findings' ? 'Findings JSON' : 'SARIF'}
                </button>
              ))}
            </div>
          </div>

          {/* Section C: Per-Source CBOM Downloads */}
          <div>
            <h2 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">Per-Source CBOM</h2>
            {inv.sources.length === 0 ? (
              <div className="text-center py-10 text-subtle-light dark:text-subtle-dark text-sm">
                No sources in this inventory.
              </div>
            ) : (
              <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-container-light dark:bg-container-dark">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Source</th>
                      <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Modality</th>
                      <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Last Scanned</th>
                      <th className="text-right px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inv.sources.map((s) => (
                      <tr key={s.id} className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark">
                        <td className="px-4 py-3">
                          <Link to={`/sources/${s.id}`} className="font-medium text-text-light dark:text-text-dark hover:text-primary">
                            {s.name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark capitalize hidden sm:table-cell">
                          {s.modality}
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark text-xs">
                          {s.last_scanned_at
                            ? <span title={new Date(s.last_scanned_at).toLocaleString()}>{formatRelative(s.last_scanned_at)}</span>
                            : <span className="text-warning">Never scanned</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/sources/${s.id}/cbom-history`}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <span className="material-icons text-sm">history</span>
                            View History
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Section B: Composite CBOM Versions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-text-light dark:text-text-dark">Composite CBOM Versions</h2>
              <span className="text-xs text-subtle-light dark:text-subtle-dark">
                {cbomVersionsTotal} version{cbomVersionsTotal !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-container-light dark:bg-container-dark">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Version</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Created</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Assets</th>
                    <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden lg:table-cell">Quantum Risk</th>
                    <th className="text-right px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Export</th>
                  </tr>
                </thead>
                <tbody>
                  {cbomVersions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-subtle-light dark:text-subtle-dark text-xs">
                        No composite CBOM versions yet. Run a scan to generate the first version.
                      </td>
                    </tr>
                  ) : (
                    cbomVersions.map((v, i) => (
                      <tr key={v.snapshot_id} className={`border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark ${i === 0 ? 'font-medium' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-text-light dark:text-text-dark">v{v.version}</span>
                            {i === 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">latest</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark text-xs hidden sm:table-cell">
                          {new Date(v.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark hidden md:table-cell">
                          {v.asset_count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-success">{v.quantum_safe} safe</span>
                            <span className="text-subtle-light dark:text-subtle-dark">/</span>
                            <span className="text-error">{v.quantum_unsafe} unsafe</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {(['cyclonedx', 'findings', 'sarif'] as const).map((fmt) => (
                              <button
                                key={fmt}
                                onClick={() => inventoryApi.downloadInventoryCompositeCbomVersion(currentOrgId, id!, v.snapshot_id, fmt, inv.name)}
                                className="px-2 py-1 text-xs rounded hover:border-primary hover:text-primary text-subtle-light dark:text-subtle-dark transition-colors"
                              >
                                {fmt === 'cyclonedx' ? 'CDX' : fmt.toUpperCase()}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                  <tr>
                    <td colSpan={5} className="p-0">
                      <div ref={cbomSentinelRef} className="h-4" />
                    </td>
                  </tr>
                  {isFetchingNextCbomPage && (
                    <tr>
                      <td colSpan={5} className="py-2 text-center text-xs text-subtle-light dark:text-subtle-dark">
                        <span className="loading loading-spinner loading-xs mr-1" />Loading…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Source Modal ─────────────────────────────────────────────── */}
      {showCreateSource && (
        <AddSourceModal
          orgId={currentOrgId}
          onClose={() => setShowCreateSource(false)}
          onCreated={(sourceId) => {
            addSourcesMutation.mutate([sourceId])
          }}
        />
      )}

      {/* ── Add Sources Modal ────────────────────────────────────────────────── */}
      {showAddSources && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => { setShowAddSources(false); setAddSourceFilter('') }} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-2xl shadow-float dark:shadow-none dark:border dark:border-border-dark p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-text-light dark:text-text-dark">Add Discovery Sources</h2>
                <button
                  onClick={() => { setShowAddSources(false); setAddSourceFilter('') }}
                  className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5"
                >
                  <span className="material-icons text-xl">close</span>
                </button>
              </div>

              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-light dark:bg-background-dark">
                <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">search</span>
                <input
                  autoFocus
                  type="text"
                  placeholder="Filter sources…"
                  value={addSourceFilter}
                  onChange={(e) => setAddSourceFilter(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark border-0 outline-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus:[box-shadow:none] focus:border-transparent"
                />
                {addSourceFilter && (
                  <button onClick={() => setAddSourceFilter('')} className="text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark">
                    <span className="material-icons text-base">close</span>
                  </button>
                )}
              </div>

              {addableSources.length === 0 ? (
                <p className="text-sm text-subtle-light dark:text-subtle-dark">
                  All available sources are already members of this inventory.
                </p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {addableSources.filter((s) => {
                    const q = addSourceFilter.toLowerCase()
                    return !q || s.name.toLowerCase().includes(q) || s.modality.toLowerCase().includes(q)
                  }).map((s) => {
                    const takenBy = takenSourceMap.get(s.id)
                    return (
                      <label
                        key={s.id}
                        title={takenBy ? `Already assigned to "${takenBy}"` : undefined}
                        className={`flex items-center gap-3 p-3 rounded-xl bg-container-light dark:bg-container-dark ${takenBy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-primary/5'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSourceIds.includes(s.id)}
                          onChange={() => !takenBy && toggleSourceSelect(s.id)}
                          disabled={!!takenBy}
                          className="w-4 h-4 accent-primary"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">{s.name}</p>
                          <p className="text-xs text-subtle-light dark:text-subtle-dark capitalize">
                            {takenBy ? `In use · ${s.modality}` : s.modality}
                          </p>
                        </div>
                      </label>
                    )
                  })}
                </div>
              )}

              {addableSources.length > 0 && (
                <div className="flex justify-between items-center pt-1">
                  <p className="text-xs text-subtle-light dark:text-subtle-dark">{selectedSourceIds.length} selected</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowAddSources(false); setAddSourceFilter('') }}
                      className="px-4 py-2 text-sm text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => addSourcesMutation.mutate(selectedSourceIds)}
                      disabled={selectedSourceIds.length === 0 || addSourcesMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                    >
                      {addSourcesMutation.isPending && (
                        <span className="material-icons text-base animate-spin">autorenew</span>
                      )}
                      Add {selectedSourceIds.length > 0 ? `(${selectedSourceIds.length})` : ''}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Edit Modal ──────────────────────────────────────────────────────── */}
      {showEditModal && inv && (
        <InventoryModal
          initial={inv}
          orgId={currentOrgId}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updated) => {
            queryClient.setQueryData(invQueryKey, (old: typeof inv) =>
              old ? { ...old, ...updated } : old
            )
          }}
        />
      )}

      {/* ── Delete Confirm Modal ─────────────────────────────────────────────── */}
      {showDeleteConfirm && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-2xl shadow-float dark:shadow-none dark:border dark:border-border-dark p-6 space-y-4">
              <h2 className="text-base font-semibold text-text-light dark:text-text-dark">Delete Inventory</h2>
              <p className="text-sm text-subtle-light dark:text-subtle-dark">
                Delete <span className="font-medium text-text-light dark:text-text-dark">{inv.name}</span>?{' '}
                Member sources are not affected.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-error text-white rounded-lg hover:bg-error/90 disabled:opacity-50"
                >
                  {deleteMutation.isPending && (
                    <span className="material-icons text-base animate-spin">autorenew</span>
                  )}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MemberSourcesTable({
  sources,
  sourceRisks,
  onRemove,
  removingId,
}: {
  sources: MemberSource[]
  sourceRisks: SourceRisk[]
  onRemove: (id: string) => void
  removingId?: string
}) {
  const { currentOrgId } = useOrgStore()
  const queryClient = useQueryClient()
  const riskMap = Object.fromEntries(sourceRisks.map((s) => [s.source_id, s]))

  const [importSourceId, setImportSourceId] = useState<string | null>(null)
  const [importSourceName, setImportSourceName] = useState('')
  const [bomStartedAt, setBomStartedAt] = useState('')
  const [bomDurationSec, setBomDurationSec] = useState('')
  const [importBomStatus, setImportBomStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [bomIsDragging, setBomIsDragging] = useState(false)
  const bomFileInputRef = useRef<HTMLInputElement>(null)

  const importBomMutation = useMutation({
    mutationFn: async (file: File) => {
      const bom = JSON.parse(await file.text())
      const body: { bom: unknown; started_at?: string; duration_ms?: number } = { bom }
      if (bomStartedAt) {
        body.started_at = new Date(bomStartedAt).toISOString()
        if (bomDurationSec) body.duration_ms = parseFloat(bomDurationSec) * 1000
      }
      return cbomApi.importBom(currentOrgId, importSourceId!, body as never)
    },
    onSuccess: (result) => {
      setImportBomStatus({
        type: 'success',
        message: `Imported. ${result.assets_loaded} crypto assets, ${result.packages_loaded} packages loaded.`,
      })
      queryClient.invalidateQueries({ queryKey: ['asset-summary-per-source', currentOrgId] })
      queryClient.invalidateQueries({ queryKey: ['asset-trend', currentOrgId] })
      queryClient.invalidateQueries({ queryKey: ['org-findings', currentOrgId] })
      queryClient.invalidateQueries({ queryKey: ['scans', currentOrgId] })
    },
    onError: (err: Error) => setImportBomStatus({ type: 'error', message: err.message }),
  })

  const openImport = (id: string, name: string) => {
    setImportSourceId(id)
    setImportSourceName(name)
    setBomStartedAt('')
    setBomDurationSec('')
    setImportBomStatus(null)
    setBomIsDragging(false)
  }

  const closeImport = () => {
    setImportSourceId(null)
    setImportBomStatus(null)
  }

  const handleBomFile = (file: File) => {
    if (!file.name.endsWith('.json')) {
      setImportBomStatus({ type: 'error', message: 'Please drop a .json file.' })
      return
    }
    setImportBomStatus(null)
    importBomMutation.mutate(file)
  }

  return (
    <>
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-light dark:border-border-dark">
              <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Source</th>
              <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Modality</th>
              <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Last Scanned</th>
              <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden lg:table-cell">Risk</th>
              <th className="text-right px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => {
              const sr = riskMap[s.id]
              const critical = sr?.summary.CRITICAL ?? 0
              const high = sr?.summary.HIGH ?? 0
              return (
                <tr
                  key={s.id}
                  className="border-b border-border-light dark:border-border-dark last:border-0 hover:bg-container-light dark:hover:bg-container-dark"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/sources/${s.id}`}
                      className="font-medium text-text-light dark:text-text-dark hover:text-primary"
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <ModalityBadge modality={s.modality as Parameters<typeof ModalityBadge>[0]['modality']} />
                  </td>
                  <td className="px-4 py-3 text-xs text-subtle-light dark:text-subtle-dark">
                    {s.last_scanned_at
                      ? <span title={new Date(s.last_scanned_at).toLocaleString()}>{new Date(s.last_scanned_at).toLocaleDateString()}</span>
                      : <span className="text-warning text-xs">Never scanned</span>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex items-center gap-1.5">
                      {critical > 0 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium">
                          {critical}C
                        </span>
                      )}
                      {high > 0 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 font-medium">
                          {high}H
                        </span>
                      )}
                      {critical === 0 && high === 0 && sr && (
                        <span className="text-xs text-subtle-light dark:text-subtle-dark">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openImport(s.id, s.name)}
                        className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/5"
                        title="Import CycloneDX BOM"
                      >
                        <span className="material-icons text-sm">upload_file</span>
                      </button>
                      <button
                        onClick={() => onRemove(s.id)}
                        disabled={removingId === s.id}
                        className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-error hover:bg-error/5 disabled:opacity-50"
                        title="Remove from inventory"
                      >
                        {removingId === s.id
                          ? <span className="material-icons text-sm animate-spin">autorenew</span>
                          : <span className="material-icons text-sm">remove_circle_outline</span>}
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ── Import CycloneDX BOM modal ──────────────────────────────────────── */}
      {importSourceId && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={closeImport} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-2xl shadow-float dark:shadow-none dark:border dark:border-border-dark p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-text-light dark:text-text-dark">Import CycloneDX BOM</h2>
                  <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">{importSourceName}</p>
                </div>
                <button onClick={closeImport} className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5">
                  <span className="material-icons text-xl">close</span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">Started at (optional)</label>
                  <input
                    type="datetime-local"
                    value={bomStartedAt}
                    onChange={(e) => setBomStartedAt(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">Duration seconds (optional)</label>
                  <input
                    type="number"
                    min="0"
                    value={bomDurationSec}
                    onChange={(e) => setBomDurationSec(e.target.value)}
                    disabled={!bomStartedAt}
                    placeholder="e.g. 120"
                    className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                  />
                </div>
              </div>

              <input
                ref={bomFileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleBomFile(file)
                  e.target.value = ''
                }}
              />
              <div
                onDragOver={(e) => { e.preventDefault(); setBomIsDragging(true) }}
                onDragLeave={() => setBomIsDragging(false)}
                onDrop={(e) => { e.preventDefault(); setBomIsDragging(false); const f = e.dataTransfer.files[0]; if (f) handleBomFile(f) }}
                onClick={() => !importBomMutation.isPending && bomFileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed transition-colors ${bomIsDragging ? 'border-primary bg-primary/5' : 'border-border-light dark:border-border-dark'} ${importBomMutation.isPending ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:border-primary hover:bg-primary/5'}`}
              >
                <span className="material-icons text-2xl text-subtle-light dark:text-subtle-dark">upload_file</span>
                {importBomMutation.isPending ? (
                  <p className="text-sm text-subtle-light dark:text-subtle-dark flex items-center gap-1.5">
                    <span className="material-icons text-base animate-spin">sync</span>
                    Importing…
                  </p>
                ) : (
                  <p className="text-sm text-subtle-light dark:text-subtle-dark">
                    Drop a CycloneDX BOM file here or <span className="text-primary">browse</span>
                  </p>
                )}
              </div>

              {importBomStatus && (
                <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${importBomStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-success' : 'bg-red-50 dark:bg-red-900/20 text-error'}`}>
                  <span className="material-icons text-base mt-0.5">{importBomStatus.type === 'success' ? 'check_circle' : 'error'}</span>
                  <span>{importBomStatus.message}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}

function PackagePostureCard({ stats, dataAgeDays }: { stats: PackageStats; dataAgeDays?: number }) {
  const pct = Math.round(stats.migration_progress)
  return (
    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light dark:border-border-dark">
        <div className="flex items-center gap-2">
          <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">inventory_2</span>
          <p className="text-sm font-semibold text-text-light dark:text-text-dark">Package Posture</p>
        </div>
        {dataAgeDays !== undefined && (
          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
            dataAgeDays <= 1 ? 'bg-success/10 text-success'
            : dataAgeDays <= 7 ? 'bg-warning/10 text-warning'
            : 'bg-error/10 text-error'
          }`}>
            <span className="material-icons text-[11px]">schedule</span>
            {dataAgeDays === 0 ? 'today' : `${dataAgeDays}d old`}
          </span>
        )}
      </div>
      <div className="p-4 space-y-4">
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-subtle-light dark:text-subtle-dark">Migration progress</span>
            <span className="font-semibold text-text-light dark:text-text-dark">{pct}%</span>
          </div>
          <div className="h-2 bg-container-light dark:bg-container-dark rounded-full overflow-hidden">
            <div className="h-full bg-success rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-2 rounded-lg bg-container-light dark:bg-container-dark">
            <p className="text-xl font-bold text-text-light dark:text-text-dark">{stats.total}</p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">Total</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-success/10 dark:bg-success/15">
            <p className="text-xl font-bold text-success">{stats.quantum_safe}</p>
            <p className="text-xs text-success/70 mt-0.5">Safe</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-error/10 dark:bg-error/15">
            <p className="text-xl font-bold text-error">{stats.quantum_unsafe}</p>
            <p className="text-xs text-error/70 mt-0.5">Unsafe</p>
          </div>
        </div>
        <div className="flex gap-4 text-xs text-subtle-light dark:text-subtle-dark">
          <span><span className="font-medium text-text-light dark:text-text-dark">{stats.direct}</span> direct</span>
          <span><span className="font-medium text-text-light dark:text-text-dark">{stats.transitive}</span> transitive</span>
        </div>
        {Object.keys(stats.by_ecosystem).length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">By Ecosystem</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_ecosystem).map(([eco, count]) => (
                <span key={eco} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-container-light dark:bg-container-dark text-xs text-text-light dark:text-text-dark">
                  <span className="font-medium">{count}</span>
                  <span className="text-subtle-light dark:text-subtle-dark">{eco}</span>
                </span>
              ))}
            </div>
          </div>
        )}
        {Object.keys(stats.by_quantum_risk ?? {}).length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">By Quantum Risk</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_quantum_risk ?? {}).map(([level, count]) => (
                <span key={level} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  level === 'VULNERABLE' ? 'bg-error/10 text-error'
                  : level === 'PARTIAL' ? 'bg-warning/10 text-warning'
                  : level === 'SAFE' ? 'bg-success/10 text-success'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
                }`}>
                  {count} {level.toLowerCase()}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CryptoPostureCard({ stats, dataAgeDays }: { stats: CryptoStats; dataAgeDays?: number }) {
  const pct = Math.round(stats.migration_progress)
  return (
    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light dark:border-border-dark">
        <div className="flex items-center gap-2">
          <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">key</span>
          <p className="text-sm font-semibold text-text-light dark:text-text-dark">Crypto Posture</p>
        </div>
        {dataAgeDays !== undefined && (
          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
            dataAgeDays <= 1 ? 'bg-success/10 text-success'
            : dataAgeDays <= 7 ? 'bg-warning/10 text-warning'
            : 'bg-error/10 text-error'
          }`}>
            <span className="material-icons text-[11px]">schedule</span>
            {dataAgeDays === 0 ? 'today' : `${dataAgeDays}d old`}
          </span>
        )}
      </div>
      <div className="p-4 space-y-4">
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-subtle-light dark:text-subtle-dark">Migration progress</span>
            <span className="font-semibold text-text-light dark:text-text-dark">{pct}%</span>
          </div>
          <div className="h-2 bg-container-light dark:bg-container-dark rounded-full overflow-hidden">
            <div className="h-full bg-success rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-2 rounded-lg bg-container-light dark:bg-container-dark">
            <p className="text-xl font-bold text-text-light dark:text-text-dark">{stats.total}</p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">Total</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-success/10 dark:bg-success/15">
            <p className="text-xl font-bold text-success">{stats.quantum_safe}</p>
            <p className="text-xs text-success/70 mt-0.5">Safe</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-error/10 dark:bg-error/15">
            <p className="text-xl font-bold text-error">{stats.quantum_unsafe}</p>
            <p className="text-xs text-error/70 mt-0.5">Unsafe</p>
          </div>
        </div>
        {Object.keys(stats.by_asset_type ?? {}).length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">By Asset Type</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_asset_type ?? {}).map(([type, count]) => (
                <span key={type} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-container-light dark:bg-container-dark text-xs text-text-light dark:text-text-dark">
                  <span className="font-medium">{count}</span>
                  <span className="text-subtle-light dark:text-subtle-dark">{type}</span>
                </span>
              ))}
            </div>
          </div>
        )}
        {Object.keys(stats.by_quantum_risk ?? {}).length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">By Quantum Risk</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_quantum_risk ?? {}).map(([level, count]) => (
                <span key={level} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  level === 'vulnerable' ? 'bg-error/10 text-error'
                  : level === 'partial' ? 'bg-warning/10 text-warning'
                  : 'bg-success/10 text-success'
                }`}>
                  {count} {level}
                </span>
              ))}
            </div>
          </div>
        )}
        {(stats.by_cert_expiry?.lt_30d ?? 0) > 0 && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-warning/10 border border-warning/20 text-xs text-warning">
            <span className="material-icons text-sm">warning_amber</span>
            {stats.by_cert_expiry.lt_30d} certificate{stats.by_cert_expiry.lt_30d !== 1 ? 's' : ''} expire within 30 days
          </div>
        )}
        {stats.by_cert_trust && Object.keys(stats.by_cert_trust).length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-2">
              Cert Trust Issues (PQC re-issue candidates)
            </p>
            <div className="flex flex-wrap gap-2">
              {stats.by_cert_trust.self_signed && stats.by_cert_trust.self_signed > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-error/10 text-error">
                  {stats.by_cert_trust.self_signed} self-signed
                </span>
              )}
              {stats.by_cert_trust.hostname_mismatch && stats.by_cert_trust.hostname_mismatch > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-error/10 text-error">
                  {stats.by_cert_trust.hostname_mismatch} hostname mismatch
                </span>
              )}
              {stats.by_cert_trust.weak_sig && stats.by_cert_trust.weak_sig > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">
                  {stats.by_cert_trust.weak_sig} weak signature
                </span>
              )}
              {stats.by_cert_trust.weak_key && stats.by_cert_trust.weak_key > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">
                  {stats.by_cert_trust.weak_key} weak key
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function riskCountClass(key: keyof RiskSummary): string {
  return `inline-flex items-center justify-center min-w-[1.5rem] px-2 py-0.5 rounded-md text-xs font-medium ${
    key === 'CRITICAL' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    : key === 'HIGH' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    : key === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
    : key === 'LOW' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    : key === 'UNKNOWN' ? 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
    : 'bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] text-subtle-light dark:text-subtle-dark'
  }`
}

function hasSummaryData(s?: RiskSummary): boolean {
  if (!s) return false
  return RISK_KEYS.some((k) => (s[k] ?? 0) > 0)
}

function RiskSummaryCard({ label, count }: { label: keyof RiskSummary; count: number }) {
  return (
    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl p-4 flex flex-col items-center gap-2">
      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${RISK_KEY_COLORS[label]}`}>
        {label}
      </span>
      <p className="text-2xl font-bold text-text-light dark:text-text-dark">{count}</p>
    </div>
  )
}

function RiskBreakdownRow({ label, icon, summary }: { label: string; icon: string; summary?: RiskSummary }) {
  return (
    <tr className="border-b border-border-light dark:border-border-dark last:border-0">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1.5 text-xs font-medium text-text-light dark:text-text-dark">
          <span className="material-icons text-sm text-subtle-light dark:text-subtle-dark">{icon}</span>
          {label}
        </div>
      </td>
      {RISK_KEYS.map((key) => (
        <td key={key} className="px-3 py-2.5 text-center">
          {(summary?.[key] ?? 0) > 0 ? (
            <span className={riskCountClass(key)}>{summary![key]}</span>
          ) : (
            <span className="text-xs text-subtle-light dark:text-subtle-dark">—</span>
          )}
        </td>
      ))}
    </tr>
  )
}

function SourceRiskRow({
  sourceRisk: s,
  sourceName,
  sourceId,
}: {
  sourceRisk: SourceRisk
  sourceName: string
  sourceId: string
}) {
  return (
    <tr className="border-b border-border-light dark:border-border-dark last:border-0 hover:bg-container-light dark:hover:bg-container-dark">
      <td className="px-4 py-3">
        <Link to={`/sources/${sourceId}`} className="font-medium text-text-light dark:text-text-dark hover:text-primary">
          {sourceName}
        </Link>
      </td>
      {RISK_KEYS.map((key) => (
        <td key={key} className="px-3 py-3 text-center">
          {(s.summary[key] ?? 0) > 0 ? (
            <span className={riskCountClass(key)}>{s.summary[key] ?? 0}</span>
          ) : (
            <span className="text-xs text-subtle-light dark:text-subtle-dark">—</span>
          )}
        </td>
      ))}
    </tr>
  )
}
